import {
  deleteEdition,
  type EditionResponse,
  type EditionStatus,
  updateEdition,
} from '@api/editions-api';
import Breadcrumbs from '@components/breadcrumbs';
import ConfirmationModal from '@components/confirmation-modal';
import EditionsSkeleton from '@components/editions-skeleton';
import { useAuth } from '@hooks/use-auth';
import { useEditions } from '@hooks/use-editions';
import {
  ClipboardList,
  Eye,
  Pause,
  Pencil,
  Play,
  Plus,
  RotateCcw,
  Trash2,
  Trophy,
  Upload,
  Users,
  Vote,
  XCircle,
} from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { sileo } from 'sileo';

import CreateEditionModal from './create-edition-modal';
import EditEditionModal from './edit-edition-modal';
import FinalizeEditionModal from './finalize-edition-modal';
import ImportEditionCsvModal from './import-edition-csv-modal';

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function getEditionLabel(edition: EditionResponse): string {
  return edition.name;
}

const STATUS_CONFIG: Record<EditionStatus, { label: string; badge: string }> = {
  open: { label: 'Open', badge: 'badge-active' },
  paused: { label: 'Paused', badge: 'badge-warning' },
  closed: { label: 'Closed', badge: 'badge-closed' },
};

/**
 * Deleting a PMS edition also removes its census — the enrollments and the voters
 * left without any other edition — so the confirmation has to say so. Voters of a
 * marketplace edition are not enrolled per edition, so there is nothing to warn
 * about there.
 */
function deleteEditionMessage(edition: EditionResponse | null): string {
  const label = edition ? getEditionLabel(edition) : '';
  const census =
    edition?.authMode === 'password'
      ? ' Its voters will be removed too, except any also taking part in another edition.'
      : '';

  return `Are you sure you want to delete "${label}"?${census} This action cannot be undone.`;
}

export default function Editions(): React.JSX.Element {
  const { editions, isLoading, error, refetch } = useEditions();
  const { user, isEditor, isAdmin } = useAuth();

  const canViewEdition = (edition: EditionResponse): boolean => {
    if (isAdmin) return true;
    return isEditor && edition.countryCode === user?.country;
  };

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [editingEdition, setEditingEdition] = useState<EditionResponse | null>(null);
  const [deletingEdition, setDeletingEdition] = useState<EditionResponse | null>(null);
  const [finalizingEdition, setFinalizingEdition] = useState<EditionResponse | null>(null);
  const [reopeningEdition, setReopeningEdition] = useState<EditionResponse | null>(null);
  const [clearWinnersOnReopen, setClearWinnersOnReopen] = useState(false);

  const handleDelete = async (edition: EditionResponse): Promise<void> => {
    await deleteEdition(edition.id);
    sileo.success({ title: 'Edition deleted successfully' });
    setDeletingEdition(null);
    refetch();
  };

  const handleTogglePause = async (edition: EditionResponse): Promise<void> => {
    const newStatus: EditionStatus = edition.status === 'open' ? 'paused' : 'open';
    try {
      await updateEdition(edition.id, { status: newStatus });
      sileo.success({
        title: `Edition ${newStatus === 'paused' ? 'paused' : 'resumed'} successfully`,
      });
      refetch();
    } catch {
      sileo.error({ title: 'Failed to update edition status' });
    }
  };

  const closeReopenModal = (): void => {
    setReopeningEdition(null);
    setClearWinnersOnReopen(false);
  };

  const handleReopen = async (edition: EditionResponse): Promise<void> => {
    try {
      await updateEdition(edition.id, {
        status: 'open',
        ...(clearWinnersOnReopen && { clearWinners: true }),
      });
      sileo.success({
        title: clearWinnersOnReopen
          ? 'Edition reopened and winners cleared'
          : 'Edition reopened successfully',
      });
      closeReopenModal();
      refetch();
    } catch {
      sileo.error({ title: 'Failed to reopen edition' });
    }
  };

  const handleFinalizeClose = (): void => {
    setFinalizingEdition(null);
  };

  const handleFinalized = (): void => {
    refetch();
  };

  const handleCreated = (): void => {
    setIsCreateOpen(false);
    refetch();
  };

  const handleImportClose = (): void => {
    setIsImportOpen(false);
    refetch();
  };

  const handleUpdated = (): void => {
    setEditingEdition(null);
    refetch();
  };

  if (isLoading) {
    return (
      <>
        <Breadcrumbs items={[{ label: 'Editions' }]} />
        <EditionsSkeleton />
      </>
    );
  }

  if (error) {
    return (
      <>
        <Breadcrumbs items={[{ label: 'Editions' }]} />
        <div className="page-error" role="alert">
          <span>Failed to load editions</span>
        </div>
      </>
    );
  }

  return (
    <>
      <Breadcrumbs items={[{ label: 'Editions' }]} />

      <div className="page-header">
        <div className="page-header__left">
          <h1 className="page-header__title">Editions</h1>
          <p className="page-header__subtitle">Manage all award editions</p>
        </div>
        <div className="page-header__actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setIsImportOpen(true)}
            data-tooltip="Upload a CSV file to create or update editions, participants, categories and nominees"
          >
            <Upload size={16} />
            Import CSV
          </button>
          {isAdmin && (
            <>
              <Link to="/categories" className="btn btn-secondary">
                <ClipboardList size={16} />
                Manage categories
              </Link>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => setIsCreateOpen(true)}
              >
                <Plus size={16} />
                New edition
              </button>
            </>
          )}
        </div>
      </div>

      {editions.length === 0 ? (
        <div className="empty-state" role="status">
          <ClipboardList size={40} className="empty-state__icon" />
          <h3 className="empty-state__title">No editions yet</h3>
          <p className="empty-state__description">
            Create your first award edition to get started.
          </p>
          {isAdmin && (
            <button type="button" className="btn btn-primary" onClick={() => setIsCreateOpen(true)}>
              <Plus size={16} />
              New edition
            </button>
          )}
        </div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>ID</th>
                <th>Year</th>
                <th>Country</th>
                <th>Voting</th>
                <th>Created</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {editions.map((edition) => (
                <tr key={edition.id}>
                  <td>
                    <strong>{getEditionLabel(edition)}</strong>
                  </td>
                  <td>
                    {edition.externalId != null ? (
                      <button
                        type="button"
                        className="badge badge-info"
                        style={{ cursor: 'pointer', border: 'none' }}
                        title="Click to copy"
                        onClick={() =>
                          void navigator.clipboard.writeText(String(edition.externalId))
                        }
                      >
                        {edition.externalId}
                      </button>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td>{edition.year}</td>
                  <td>{edition.country}</td>
                  <td>
                    <span className={`badge ${STATUS_CONFIG[edition.status].badge}`}>
                      {STATUS_CONFIG[edition.status].label}
                    </span>
                  </td>
                  <td>{formatDate(edition.createdAt)}</td>
                  <td>
                    <div className="table-actions">
                      {canViewEdition(edition) && (
                        <>
                          <Link
                            to={`/editions/${edition.id}/categories`}
                            className="btn btn-ghost btn-icon btn-sm"
                            data-tooltip="View categories"
                            aria-label="View categories"
                          >
                            <Eye size={16} />
                          </Link>
                          <Link
                            to={`/editions/${edition.id}/nominees`}
                            className="btn btn-ghost btn-icon btn-sm"
                            data-tooltip="View all nominees"
                            aria-label="View all nominees"
                          >
                            <Users size={16} />
                          </Link>
                          <Link
                            to={`/editions/${edition.id}/votes`}
                            className="btn btn-ghost btn-icon btn-sm"
                            data-tooltip="View all votes"
                            aria-label="View all votes"
                          >
                            <Vote size={16} />
                          </Link>
                          {edition.hasWinners && (
                            <Link
                              to={`/editions/${edition.id}/hall-of-fame`}
                              className="btn btn-ghost btn-icon btn-sm"
                              data-tooltip="View Hall of Fame"
                              aria-label="View Hall of Fame"
                            >
                              <Trophy size={16} />
                            </Link>
                          )}
                        </>
                      )}
                      {isAdmin && (
                        <>
                          <button
                            type="button"
                            className="btn btn-ghost btn-icon btn-sm"
                            data-tooltip="Edit edition"
                            aria-label="Edit edition"
                            onClick={() => setEditingEdition(edition)}
                          >
                            <Pencil size={16} />
                          </button>
                          <button
                            type="button"
                            className="btn btn-ghost btn-icon btn-sm"
                            data-tooltip="Delete edition"
                            aria-label="Delete edition"
                            onClick={() => setDeletingEdition(edition)}
                          >
                            <Trash2 size={16} />
                          </button>
                          {edition.status !== 'closed' && (
                            <button
                              type="button"
                              className="btn btn-ghost btn-icon btn-sm"
                              data-tooltip={
                                edition.status === 'open' ? 'Pause voting' : 'Resume voting'
                              }
                              aria-label={
                                edition.status === 'open' ? 'Pause voting' : 'Resume voting'
                              }
                              onClick={() => void handleTogglePause(edition)}
                            >
                              {edition.status === 'open' ? <Pause size={16} /> : <Play size={16} />}
                            </button>
                          )}
                          {edition.status !== 'closed' && (
                            <button
                              type="button"
                              className="btn btn-ghost btn-icon btn-sm"
                              data-tooltip="Finalize edition"
                              aria-label="Finalize edition"
                              onClick={() => setFinalizingEdition(edition)}
                            >
                              <XCircle size={16} />
                            </button>
                          )}
                          {edition.status === 'closed' && (
                            <button
                              type="button"
                              className="btn btn-ghost btn-icon btn-sm"
                              data-tooltip="Reopen edition"
                              aria-label="Reopen edition"
                              onClick={() => setReopeningEdition(edition)}
                            >
                              <RotateCcw size={16} />
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <CreateEditionModal isOpen={isCreateOpen} onClose={handleCreated} />

      <EditEditionModal
        isOpen={editingEdition !== null}
        onClose={handleUpdated}
        edition={editingEdition}
      />

      <ConfirmationModal
        isOpen={deletingEdition !== null}
        onClose={() => setDeletingEdition(null)}
        onConfirm={() => {
          if (deletingEdition) return handleDelete(deletingEdition);
        }}
        title="Delete edition"
        message={deleteEditionMessage(deletingEdition)}
        confirmLabel="Delete"
        variant="danger"
      />

      <FinalizeEditionModal
        edition={finalizingEdition}
        isOpen={finalizingEdition !== null}
        onClose={handleFinalizeClose}
        onFinalized={handleFinalized}
      />

      <ConfirmationModal
        isOpen={reopeningEdition !== null}
        onClose={closeReopenModal}
        onConfirm={() => {
          if (reopeningEdition) return handleReopen(reopeningEdition);
        }}
        title="Reopen edition"
        message={`Are you sure you want to reopen "${reopeningEdition ? getEditionLabel(reopeningEdition) : ''}"? Voting will be enabled again.${reopeningEdition?.hasWinners ? '' : ' Winners stay marked.'}`}
        confirmLabel="Reopen"
      >
        {reopeningEdition?.hasWinners && (
          <label className="confirmation-modal__option">
            <input
              type="checkbox"
              checked={clearWinnersOnReopen}
              onChange={(e) => setClearWinnersOnReopen(e.target.checked)}
            />
            <span>Clear the current winners of this edition</span>
          </label>
        )}
      </ConfirmationModal>

      <ImportEditionCsvModal isOpen={isImportOpen} onClose={handleImportClose} />
    </>
  );
}
